import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { diContainer } from 'src/dependencyInjection/diContainer';
import { DI_TYPES } from 'src/dependencyInjection/diTypes';
import { IHttpService } from 'src/services/HttpService/IHttpService';
import { Button } from 'src/shadcn/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/shadcn/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shadcn/components/ui/popover';
import TrieSearch from 'trie-search';
import { z } from 'zod';
import { FixedSizeList as ReactWindowList } from 'react-window';
import { vehicle_makes_and_models } from './makes_and_models';

export interface ICarModelSelectorProps {
  value: string;
  setValue: (newValue: string) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  makeId: string | undefined;
}

export const CarModelSelector: React.FunctionComponent<
  ICarModelSelectorProps
> = props => {
  const { value, setValue, makeId } = props;
  const [searchVal, setSearchVal] = React.useState<string>();
  const [open, setOpen] = React.useState(false);
  // const { models, isLoading, error } = useGetCarModels(makeId);
  const models = React.useMemo(() => {
    return vehicle_makes_and_models.results
      .find(make => {
        return make.make.label.toLowerCase() === makeId?.toLowerCase();
      })
      ?.models.map(model => {
        return {
          Model_ID: model.value,
          Model_Name: model.label,
        };
      });
  }, []);
  const trie = React.useMemo(() => {
    const trie = new TrieSearch<z.infer<typeof modelRowValidator>>(
      'Model_Name',
      {
        min: 1,
      }
    );
    trie.addAll(models || []);
    return trie;
  }, [models]);

  const items =
    (searchVal?.length || 0) > 1 ? trie.search(searchVal || '') : models;
  const isCreateNewEnabled = Boolean(
    searchVal?.length && searchVal.length > 1 && items?.length !== 1
  );
  const triggerText = value ? value : 'Select model...';
  return (
    <Popover
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          setSearchVal(undefined);
        }
        setOpen(isOpen);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-full justify-between'
          data-testid='car-model-selector-combobox'
        >
          <div className='truncate' title={triggerText}>
            {triggerText}
          </div>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[250px] p-0'
        container={props.containerRef?.current}
      >
        <Command shouldFilter={false} className=''>
          <CommandInput
            placeholder='Search models...'
            onValueChange={newValue => {
              setSearchVal(newValue);
            }}
            value={searchVal}
          />
          <CommandList>
            <CommandEmpty>No makes found.</CommandEmpty>
            {isCreateNewEnabled && (
              <CommandGroup heading='Create new' className='p-0'>
                <CommandItem
                  key={searchVal}
                  value={searchVal}
                  onSelect={currentValue => {
                    setOpen(false);
                    setSearchVal(undefined);
                    setValue((searchVal || '').trim());
                  }}
                  title={`Create item: ${searchVal}`}
                >
                  <span className='overflow-hidden text-ellipsis whitespace-nowrap'>
                    {`Create item: ${searchVal}`}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            {items && Boolean(items?.length) && (
              <CommandGroup heading='Select exisitng' className='p-0'>
                <ReactWindowList
                  height={Math.min(
                    isCreateNewEnabled ? 204 : 264,
                    35 * items.length
                  )}
                  itemCount={items.length}
                  itemData={items.map(item => {
                    return {
                      item,
                      onSelect: () => {
                        setValue(item.Model_Name);
                        setOpen(false);
                        setSearchVal(undefined);
                      },
                    };
                  })}
                  itemSize={35}
                  width={250}
                >
                  {Row}
                </ReactWindowList>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

function useGetCarModels(makeId: string | undefined) {
  const { data, error, isLoading } = useQuery({
    queryKey: [makeId || ''],
    queryFn: async () => {
      if (!makeId) {
        return null;
      }
      const url = `https://vpic.nhtsa.dot.gov/api/vehicles/GetModelsForMake/${makeId}?format=json`;
      const response = await diContainer
        .get<IHttpService>(DI_TYPES.HttpService)
        .send({
          type: 'GET',
          url,
          response: z.object({
            Count: z.number(),
            Message: z.string(),
            SearchCriteria: z.string().nullish(),
            Results: z.array(modelRowValidator),
          }),
        });
      return response;
    },
  });
  return {
    models: data?.Results,
    error,
    isLoading,
  };
}

const modelRowValidator = z.object({
  // Make_ID: z.number(),
  // Make_Name: z.string(),
  // Model_ID: z.number(),
  Model_ID: z.string(),
  Model_Name: z.string(),
});

interface RowProps {
  style: React.CSSProperties;
  index: number;
  data: {
    item: z.infer<typeof modelRowValidator>;
    onSelect: () => void;
  }[];
}
const Row = React.forwardRef<HTMLDivElement, RowProps>(
  ({ style, data, index }, ref) => {
    const item = data[index].item;
    return (
      <CommandItem
        ref={ref}
        style={style}
        key={item.Model_ID}
        value={item.Model_Name}
        onSelect={data[index].onSelect}
        title={item.Model_Name}
      >
        <span className='overflow-hidden text-ellipsis whitespace-nowrap'>
          {item.Model_Name}
        </span>
      </CommandItem>
    );
  }
);
